import { defineStore } from "pinia";
import {
  computed,
  ComputedRef,
  reactive,
  Reactive,
  Ref,
  ref,
  watch
} from "vue";
import axios from "axios";
import { io } from "socket.io-client";
import * as Sentry from "@sentry/vue";

import elementInputs from "@/data/generated/inputLocation.json";
import { useCalculationStore } from "./calculationStore";
import { useAuthStore } from "./authStore";
import { useSpaceStore } from "./spaceStore";
import { useRouter } from "vue-router";
import { useAxiosStore } from "./axiosStore";
import { useSocketStore } from "./socketStore";
import { organization } from "@/types/global";

export const useMainStore = defineStore("mainStore", () => {
  const spaceStore = useSpaceStore();
  const authStore = useAuthStore();
  const socketStore = useSocketStore();
  const calculationStore = useCalculationStore();
  const router = useRouter();

  const copyData: Reactive<copyData> = reactive({
    allElements: [],
    locations: [],
    id: "",
    name: "",
    spaceId: "",
    variantName: "",
    extra: undefined
  });

  const activeSpaceId = ref<string | null>(null);

  const occupiedFields = ref<Record<string, Record<string, string>>>({});
  const activeUsers = ref<Record<string, string>>({});
  const activeElements = computed(() => {
    return Object.entries(activeUsers.value).reduce(
      (result, [userId, elementId]) => {
        result[elementId] = userId;
        return result;
      },
      {} as Record<string, string>
    );
  });

  const calculationSpaces = ref([]);
  const organizations: Ref<organization[]> = ref([]);
  const collaborationUsers = computed(() => {
    const result = {};

    for (const space of calculationSpaces.value) {
      for (const user of space.users) {
        result[user.id] = user;
        result[user.id].initials = user.first_name[0] + user.last_name[0];
      }
    }
    return result;
  });

  function getInitials(id: string) {
    if (collaborationUsers.value[id]) {
      return collaborationUsers.value[id].initials;
    }
  }

  const dragItem = ref(null);

  async function loadCalculationSpaces() {
    return axios
      .get("/api/calculation-spaces")
      .then((response) => {
        calculationSpaces.value = response.data;
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  async function loadOrganizations() {
    return axios
      .get("/api/organization/list")
      .then((response) => {
        organizations.value = response.data;
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  function createSpace(name: string, description: string) {
    axios
      .post("/api/calculation-spaces", {
        name: name,
        description: description
      })
      .then((response) => {
        calculationSpaces.value.push(response.data);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  function addCollaborator(email: string, space: number) {
    axios
      .post("/api/calculation-spaces/" + space + "/add-user", {
        email: email
      })
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  async function openSpace(spaceId: string) {
    if (calculationSpaces.value.find((space) => space.id === spaceId)) {
      if (activeSpaceId.value !== null) {
        // Leave current space
        socketStore.socket.emit("leave-space", activeSpaceId.value);
        spaceStore.clearData();
      }

      activeSpaceId.value = spaceId;
      console.log("Opening space:", spaceId);
      socketStore.socket.emit("enter-space", spaceId, (activeData) => {
        console.log("Active data:", activeData);
        for (const data of activeData) {
          if (data.focusElement) {
            activeUsers.value[data.user] = data.focusElement;

            if (data.focusField) {
              occupiedFields[data.focusElement] = {
                ...occupiedFields[data.focusElement],
                [data.focusField]: data.user
              };

              calculationStore.focusElementSocket(
                data.focusElement,
                data.focusField,
                data.user
              );
            }
          }
        }
      });
      authStore.globalLoading = true;
      return spaceStore.loadAllElements().then(() => {
        authStore.globalLoading = false;
      });
    } else {
      console.log("Invalid space ID");
      leaveSpace();
      Promise.reject();
    }
  }

  async function leaveSpace() {
    await router.replace({ path: "/" });
    await spaceStore.checkIfEverythingIsCorrectlyStored();
    socketStore.socket.emit("leave-space", activeSpaceId.value);

    if (calculationStore.calculating) {
      await new Promise((resolve) => {
        watch(
          () => calculationStore.calculating,
          () => {
            resolve(null);
          }
        );
      });
    }

    console.log("User left space, clearing data");

    activeSpaceId.value = null;
    spaceStore.clearData();
  }

  async function acceptInvitation(spaceId: string) {
    axios
      .post(`/api/calculation-spaces/${spaceId}/accept-invitation`, { spaceId })
      .then(() => {
        for (const space of calculationSpaces.value) {
          if (space.id === spaceId) {
            space.state = 1;
            break;
          }
        }
      });
  }

  async function declineInvitation(spaceId: string) {
    axios
      .post(`/api/calculation-spaces/${spaceId}/decline-invitation`, {
        spaceId
      })
      .then(() => {
        calculationSpaces.value = calculationSpaces.value.filter(
          (space) => space.id !== spaceId
        );
      });
  }

  async function acceptOrganizationInvitation(
    organizationId: number,
    callback: () => void
  ) {
    return axios
      .post(`/api/organization/accept/` + organizationId, {
        organizationId: organizationId
      })
      .then(() => {
        callback();
      });
  }

  async function declineOrganizationInvitation(organizationId: number) {
    return axios.post(`/api/organization/decline`, {
      organizationId: organizationId
    });
  }

  async function removeSpace(spaceId: string) {
    await axios.post(`/api/calculation-spaces/${spaceId}/leave`);
    calculationSpaces.value = calculationSpaces.value.filter(
      (space) => space.id !== spaceId
    );
  }

  function organizationRights(organizationId: string) {
    const orga = organizations.value.find(
      (organization) =>
        Number(organization.organization.id) === Number(organizationId)
    );

    return orga?.rights;
  }

  return {
    dragItem,
    loadCalculationSpaces,
    loadOrganizations,
    organizations,
    calculationSpaces,
    createSpace,
    addCollaborator,
    occupiedFields,
    activeElements,
    collaborationUsers,
    activeSpaceId,
    copyData,
    openSpace,
    leaveSpace,
    activeUsers,
    getInitials,
    acceptInvitation,
    declineInvitation,
    removeSpace,
    acceptOrganizationInvitation,
    declineOrganizationInvitation,
    organizationRights
  };
});
